// Tab "Scenes": scenes met nummer, omschrijving, locatie en dag/nacht. Velden slaan op bij verlaten.
import React, { useEffect, useState } from 'react';
import { Box, IconButton, InputBase, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { meldFout } from '../../../utils/apiFout';
import { api, nieuweScene, wijzigScene, LIJN, STIL } from '../breakdownApi';
import { Kaart, Leeg, Primair, Rij, Uitleg } from '../onderdelen';

function Veld({ waarde, onBewaar, ...props }) {
  const [tekst, setTekst] = useState(waarde ?? '');
  useEffect(() => { setTekst(waarde ?? ''); }, [waarde]);
  return (
    <InputBase value={tekst} onChange={(e) => setTekst(e.target.value)}
      onBlur={() => { if (String(tekst) !== String(waarde ?? '')) onBewaar(tekst); }}
      sx={{ fontSize: 13.5, px: 0.75, borderRadius: 1, '&:hover': { bgcolor: '#f3f4f6' }, '&.Mui-focused': { bgcolor: '#fff', outline: `1px solid ${LIJN}` } }}
      {...props} />
  );
}

export default function ScenesTab({ productie, scenes, setScenes }) {
  const volgend = scenes.reduce((m, s) => Math.max(m, s.number), 0) + 1;

  const vervang = (s) => setScenes((lijst) => lijst.map((x) => (x.id === s.id ? s : x)));

  async function voegToe() {
    try { setScenes([...scenes, await nieuweScene(productie.id, { number: volgend, heading: '' })]); }
    catch (e) { meldFout(e, 'Scene toevoegen'); }
  }

  async function bewaar(s, veld, waarde) {
    try { vervang(await wijzigScene(s.id, { [veld]: veld === 'number' ? Number(waarde) : waarde })); }
    catch (e) { meldFout(e, 'Scene opslaan'); }
  }

  async function dagNacht(s, waarde) {
    if (!waarde || waarde === s.day_night) return;
    const vorige = s.day_night;
    try {
      vervang(await wijzigScene(s.id, { day_night: waarde }));
      biedOngedaanAan(`Scene ${s.number}: ${waarde}`, async () => vervang(await wijzigScene(s.id, { day_night: vorige })));
    } catch (e) { meldFout(e, 'Scene opslaan'); }
  }

  async function verwijder(s) {
    const ok = await bevestig({ titel: `Scene ${s.number} verwijderen?`, tekst: 'De scene gaat ook van elk stripboard af. Items en shots met dit scenenummer blijven staan.', knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      await api(`/scenes/${s.id}`, { method: 'DELETE', wat: 'Scene verwijderen' });
      setScenes((lijst) => lijst.filter((x) => x.id !== s.id));
    } catch (e) { meldFout(e, 'Scene verwijderen'); }
  }

  const gesorteerd = [...scenes].sort((a, b) => a.number - b.number);
  return (
    <Box>
      <Uitleg>Scenes vormen de basis voor de shotlist en het stripboard. Laat ze maken via het tabblad Script, of voeg ze hier toe.</Uitleg>
      {gesorteerd.length === 0 ? <Leeg>Nog geen scenes.</Leeg> : (
        <Kaart>
          {gesorteerd.map((s) => (
            <Rij key={s.id}>
              <Veld waarde={s.number} type="number" onBewaar={(v) => v !== '' && bewaar(s, 'number', v)}
                inputProps={{ min: 0, 'aria-label': 'Scenenummer' }} sx={{ width: 64, fontSize: 14, fontWeight: 700, px: 0.75 }} />
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Veld waarde={s.heading} placeholder="Omschrijving" onBewaar={(v) => bewaar(s, 'heading', v)} inputProps={{ 'aria-label': 'Omschrijving' }} />
                <Veld waarde={s.location} placeholder="Locatie" onBewaar={(v) => bewaar(s, 'location', v)} inputProps={{ 'aria-label': 'Locatie' }} sx={{ fontSize: 12.5, color: STIL }} />
              </Box>
              <ToggleButtonGroup size="small" exclusive value={s.day_night} onChange={(_, v) => dagNacht(s, v)}>
                <ToggleButton value="dag" sx={{ textTransform: 'none', py: 0.25 }}>Dag</ToggleButton>
                <ToggleButton value="nacht" sx={{ textTransform: 'none', py: 0.25 }}>Nacht</ToggleButton>
              </ToggleButtonGroup>
              <IconButton size="small" aria-label={`Scene ${s.number} verwijderen`} onClick={() => verwijder(s)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Rij>
          ))}
        </Kaart>
      )}
      <Primair sx={{ mt: 1.5 }} onClick={voegToe}>+ Scene {volgend}</Primair>
      {gesorteerd.length > 0 && <Typography sx={{ mt: 1, fontSize: 12, color: STIL }}>Wijzigingen slaan op zodra je het veld verlaat.</Typography>}
    </Box>
  );
}
